import {
  AlertCard,
  AlertEpisode,
  EpisodeStatus,
  FloorEventSeverity,
  KitchenAlert,
  KitchenAlertType,
  KitchenCamera
} from '../types';
import { KitchenPlan } from './plan';

/**
 * VAA incidents into the episodes and cards the Alerts tab shows.
 *
 * `buildEpisodes` coalesces close-together incidents on one camera into one
 * episode; `buildCards` folds a quick recurrence of the same episode into one row.
 */

/** Consecutive incidents of the same type+camera this close together are one episode. */
const COALESCE_GAP_MS = 120_000;

/** An episode is active while its latest incident is incomplete and this recent. */
const ACTIVE_GRACE_MS = 120_000;

/** A new episode of the same type+camera this soon after the last one clears joins its card. */
const RECURRENCE_WINDOW_MS = 10 * 60_000;

const SEVERITY_BY_TYPE: Record<KitchenAlertType, FloorEventSeverity> = {
  PPE: 'critical',
  RESTRICTED_AREA: 'critical',
  OVERCROWDING: 'warning',
  PROXIMITY: 'warning',
  OTHER: 'info'
};

const SEVERITY_RANK: Record<FloorEventSeverity, number> = { critical: 3, warning: 2, info: 1 };

const groupKey = (type: KitchenAlertType, sensorId: string): string => `${type}:${sensorId}`;

/** Last write wins by latest `end`, so a BA re-emission of an incident id supersedes the earlier copy. */
const upsertById = (alerts: KitchenAlert[]): KitchenAlert[] => {
  const byId = new Map<string, KitchenAlert>();
  for (const alert of alerts) {
    const existing = byId.get(alert.id);
    if (!existing || Date.parse(alert.end) >= Date.parse(existing.end)) {
      byId.set(alert.id, alert);
    }
  }
  return [...byId.values()];
};

const toEpisode = (cluster: KitchenAlert[]): AlertEpisode => {
  const first = cluster[0];
  const latest = cluster.reduce((a, b) => (Date.parse(b.end) >= Date.parse(a.end) ? b : a));
  return {
    id: first.id,
    type: first.type,
    category: latest.category,
    sensorId: first.sensorId,
    start: Date.parse(first.start),
    end: Date.parse(latest.end),
    complete: latest.complete,
    objectCount: latest.objectCount,
    verdict: latest.verdict,
    incidentIds: cluster.map((alert) => alert.id)
  };
};

/** Rule 2 of the floor contract's not-annoying rules: gaps of <= 120s on one type+camera coalesce. */
export const buildEpisodes = (alerts: KitchenAlert[]): AlertEpisode[] => {
  const sorted = upsertById(alerts).sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  const byGroup = new Map<string, KitchenAlert[]>();
  for (const alert of sorted) {
    const key = groupKey(alert.type, alert.sensorId);
    const group = byGroup.get(key);
    if (group) {
      group.push(alert);
    } else {
      byGroup.set(key, [alert]);
    }
  }

  const episodes: AlertEpisode[] = [];
  for (const group of byGroup.values()) {
    let cluster: KitchenAlert[] = [];
    for (const alert of group) {
      const last = cluster.at(-1);
      if (last && Date.parse(alert.start) - Date.parse(last.end) > COALESCE_GAP_MS) {
        episodes.push(toEpisode(cluster));
        cluster = [];
      }
      cluster.push(alert);
    }
    if (cluster.length > 0) {
      episodes.push(toEpisode(cluster));
    }
  }
  return episodes.sort((a, b) => a.start - b.start);
};

/** Rule 3: active while the latest incident is open and recent; otherwise cleared. */
export const episodeStatus = (episode: AlertEpisode, now: number): EpisodeStatus =>
  !episode.complete && now - episode.end < ACTIVE_GRACE_MS ? 'active' : 'cleared';

const episodeClearedAt = (episode: AlertEpisode): number => (episode.complete ? episode.end : episode.end + ACTIVE_GRACE_MS);

/** A history-only incomplete episode with no live channel confirming it either way ages out after this long. */
const HISTORY_STALE_MS = 10 * 60_000;

/** A history-only episode (no live incident) left incomplete this long reads as cleared. */
export const ageHistoryOnlyEpisodes = (
  episodes: AlertEpisode[],
  liveIncidentIds: ReadonlySet<string>,
  now: number
): AlertEpisode[] =>
  episodes.map((episode) => {
    const isLive = episode.incidentIds.some((incidentId) => liveIncidentIds.has(incidentId));
    if (episode.complete || isLive || now - episode.end < HISTORY_STALE_MS) {
      return episode;
    }
    return { ...episode, complete: true };
  });

/** The episode's room, resolved like `episodeZoneId`; falls back to the sensorId when none is known. */
const episodeRoomKey = (
  episode: AlertEpisode,
  kitchenPlan?: KitchenPlan,
  streamIdsBySensorId?: ReadonlyMap<string, string | null>
): string => {
  if (!kitchenPlan) {
    return episode.sensorId;
  }
  const room = resolveCameraForSensor(episode.sensorId, kitchenPlan, streamIdsBySensorId ?? new Map())?.room;
  return room ?? episode.sensorId;
};

/** Rule 6: episodes of the same type+room fold into one card while each recurs within 10 min of the last clearing. */
export const buildCards = (
  episodes: AlertEpisode[],
  kitchenPlan?: KitchenPlan,
  streamIdsBySensorId?: ReadonlyMap<string, string | null>
): AlertCard[] => {
  const byGroup = new Map<string, AlertEpisode[]>();
  for (const episode of [...episodes].sort((a, b) => a.start - b.start)) {
    const key = groupKey(episode.type, episodeRoomKey(episode, kitchenPlan, streamIdsBySensorId));
    const group = byGroup.get(key);
    if (group) {
      group.push(episode);
    } else {
      byGroup.set(key, [episode]);
    }
  }

  const cards: AlertCard[] = [];
  for (const [key, group] of byGroup) {
    let current: AlertEpisode[] = [];
    for (const episode of group) {
      const previous = current.at(-1);
      if (previous && episode.start - episodeClearedAt(previous) > RECURRENCE_WINDOW_MS) {
        cards.push({ key: `${key}:${current[0].id}`, type: current[0].type, sensorId: current[0].sensorId, episodes: current });
        current = [];
      }
      current.push(episode);
    }
    if (current.length > 0) {
      cards.push({ key: `${key}:${current[0].id}`, type: current[0].type, sensorId: current[0].sensorId, episodes: current });
    }
  }
  return cards;
};

export const latestEpisode = (card: AlertCard): AlertEpisode => card.episodes[card.episodes.length - 1];

export const cardStatus = (card: AlertCard, now: number): EpisodeStatus => episodeStatus(latestEpisode(card), now);

export const episodeSeverity = (type: KitchenAlertType): FloorEventSeverity => SEVERITY_BY_TYPE[type];

export const severityRank = (severity: FloorEventSeverity): number => SEVERITY_RANK[severity];

/** Rejected-by-VLM episodes never draw attention (rule 8); they stay in the log only. */
export const isRejected = (episode: AlertEpisode): boolean => episode.verdict === 'rejected';

/**
 * The plan camera behind a sensor id. A BA `sensorId` is a plan camera
 * directly; an RT-VLM `sensorId` is already a stream id, so it takes a
 * reverse lookup through the live camera list to find which plan camera is
 * streaming it.
 */
export const resolveCameraForSensor = (
  sensorId: string,
  kitchenPlan: KitchenPlan,
  streamIdsBySensorId: ReadonlyMap<string, string | null>
): KitchenCamera | null => {
  const direct = kitchenPlan.cameraById.get(sensorId);
  if (direct) {
    return direct;
  }
  for (const [candidateSensorId, streamId] of streamIdsBySensorId) {
    if (streamId === sensorId) {
      return kitchenPlan.cameraById.get(candidateSensorId) ?? null;
    }
  }
  return null;
};

/** The zone an episode belongs to, via `resolveCameraForSensor`. */
export const episodeZoneId = (
  episode: AlertEpisode,
  kitchenPlan: KitchenPlan,
  streamIdsBySensorId: ReadonlyMap<string, string | null>
): string | null => resolveCameraForSensor(episode.sensorId, kitchenPlan, streamIdsBySensorId)?.zoneId ?? null;

/** A live event's zone: its room if matched on the plan, else `resolveCameraForSensor`. */
export const resolveZoneId = (
  room: string | null,
  sensorId: string,
  kitchenPlan: KitchenPlan,
  streamIdsBySensorId: ReadonlyMap<string, string | null>
): string | null => {
  if (room) {
    for (const [zoneId, rooms] of kitchenPlan.roomsByZone) {
      if (rooms.includes(room)) {
        return zoneId;
      }
    }
  }
  return resolveCameraForSensor(sensorId, kitchenPlan, streamIdsBySensorId)?.zoneId ?? null;
};

/** The alert type an incident category stands for; the first matching rule wins. */
export const categoryToType = (category: string): KitchenAlertType => {
  const normalized = category.toLowerCase();
  if (normalized.includes('fov count')) {
    return 'OVERCROWDING';
  }
  if (normalized.includes('proximity')) {
    return 'PROXIMITY';
  }
  if (normalized.includes('restricted') || normalized.includes('confined')) {
    return 'RESTRICTED_AREA';
  }
  if (normalized.startsWith('ppe') || normalized.includes('hairnet') || normalized.includes('mask')) {
    return 'PPE';
  }
  return 'OTHER';
};

const ALERT_TYPE_LABEL: Record<KitchenAlertType, string> = {
  OVERCROWDING: 'Overcrowding',
  PROXIMITY: 'Proximity',
  PPE: 'PPE',
  RESTRICTED_AREA: 'Restricted area',
  OTHER: 'Other'
};

export const alertTypeLabel = (type: KitchenAlertType): string => ALERT_TYPE_LABEL[type];

export interface RecurrenceContext {
  /** This episode's position among every same-type, same-camera episode today (1 = the first). */
  ordinal: number;
  /** When the previous such episode cleared, or null for the first. */
  previousClearedAt: number | null;
}

/** For "3rd overcrowding here today · last one ended 25 min ago" in the detail dialog. */
export const recurrenceContext = (episodes: AlertEpisode[], target: AlertEpisode): RecurrenceContext => {
  const sameGroup = episodes
    .filter((episode) => episode.type === target.type && episode.sensorId === target.sensorId)
    .sort((a, b) => a.start - b.start);
  const index = sameGroup.findIndex((episode) => episode.id === target.id);
  const previous = index > 0 ? sameGroup[index - 1] : null;
  return { ordinal: index + 1, previousClearedAt: previous ? episodeClearedAt(previous) : null };
};
